import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CreateDemandReviewExhibit,
  CreateDemandReviewWorkspace,
  DemandReviewCitation,
  DemandReviewCitationFilters,
  DemandReviewExhibit,
  DemandReviewWorkspace,
  MatchCitationsResponse,
  ProofreadingJob,
  UpdateDemandReviewExhibit,
  UpdateDemandReviewWorkspace,
  VerifyAllCitationsResponse,
  WorkflowStatusResponse,
} from '../types/demand-review-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const demandReviewApiClient = createApi({
  reducerPath: 'demandReviewApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'DemandReviewWorkspaces',
    'DemandReviewWorkspace',
    'DemandReviewExhibits',
    'DemandReviewCitations',
    'ProofreadingResults',
  ] as const,
  endpoints: (builder) => ({
    // Workspace endpoints
    listDemandReviewWorkspaces: builder.query<
      DemandReviewWorkspace[],
      { matterId: string }
    >({
      query: ({ matterId }) => `api/matters/${matterId}/demand-review/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'DemandReviewWorkspaces', id: matterId },
        ...(result ?? []).map((ws) => ({
          type: 'DemandReviewWorkspace' as const,
          id: ws.id,
        })),
      ],
    }),

    getDemandReviewWorkspace: builder.query<
      DemandReviewWorkspace,
      { matterId: string; workspaceId: string; includeMarkdown?: boolean }
    >({
      query: ({ matterId, workspaceId, includeMarkdown = false }) =>
        `api/matters/${matterId}/demand-review/${workspaceId}/?include_markdown=${includeMarkdown}`,
      providesTags: (result, error, { workspaceId }) => [
        { type: 'DemandReviewWorkspace', id: workspaceId },
      ],
    }),

    createDemandReviewWorkspace: builder.mutation<
      DemandReviewWorkspace,
      { matterId: string; data: CreateDemandReviewWorkspace }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/demand-review/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'DemandReviewWorkspaces', id: matterId },
      ],
    }),

    updateDemandReviewWorkspace: builder.mutation<
      DemandReviewWorkspace,
      {
        matterId: string;
        workspaceId: string;
        data: UpdateDemandReviewWorkspace;
      }
    >({
      query: ({ matterId, workspaceId, data }) => ({
        url: `api/matters/${matterId}/demand-review/${workspaceId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { matterId, workspaceId }) => [
        { type: 'DemandReviewWorkspace', id: workspaceId },
        { type: 'DemandReviewWorkspaces', id: matterId },
      ],
    }),

    deleteDemandReviewWorkspace: builder.mutation<
      void,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) => ({
        url: `api/matters/${matterId}/demand-review/${workspaceId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'DemandReviewWorkspaces', id: matterId },
      ],
    }),

    uploadDemand: builder.mutation<
      DemandReviewWorkspace,
      { matterId: string; workspaceId: string; file: File }
    >({
      query: ({ matterId, workspaceId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `api/matters/${matterId}/demand-review/${workspaceId}/upload-demand/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { matterId, workspaceId }) => [
        { type: 'DemandReviewWorkspace', id: workspaceId },
        { type: 'DemandReviewWorkspaces', id: matterId },
      ],
    }),

    startWorkflow: builder.mutation<
      DemandReviewWorkspace,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) => ({
        url: `api/matters/${matterId}/demand-review/${workspaceId}/start-workflow/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'DemandReviewWorkspace', id: workspaceId },
      ],
    }),

    getWorkflowStatus: builder.query<
      WorkflowStatusResponse,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) =>
        `api/matters/${matterId}/demand-review/${workspaceId}/workflow-status/`,
      // Don't use tag-based caching for this since we poll it
    }),

    matchCitations: builder.mutation<
      MatchCitationsResponse,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) => ({
        url: `api/matters/${matterId}/demand-review/${workspaceId}/match-citations/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'DemandReviewCitations', id: workspaceId },
        { type: 'DemandReviewWorkspace', id: workspaceId },
      ],
    }),

    verifyAllCitations: builder.mutation<
      VerifyAllCitationsResponse,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) => ({
        url: `api/matters/${matterId}/demand-review/${workspaceId}/verify-all-citations/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'DemandReviewCitations', id: workspaceId },
        { type: 'DemandReviewWorkspace', id: workspaceId },
      ],
    }),

    // Proofreading results endpoint
    getProofreadingResults: builder.query<
      ProofreadingJob,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) =>
        `api/matters/${matterId}/demand-review/${workspaceId}/proofreading-results/`,
      providesTags: (result, error, { workspaceId }) => [
        { type: 'ProofreadingResults', id: workspaceId },
      ],
    }),

    // Exhibit endpoints
    listDemandReviewExhibits: builder.query<
      DemandReviewExhibit[],
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) =>
        `api/matters/${matterId}/demand-review/${workspaceId}/exhibits/`,
      providesTags: (result, error, { workspaceId }) => [
        { type: 'DemandReviewExhibits', id: workspaceId },
      ],
    }),

    getDemandReviewExhibit: builder.query<
      DemandReviewExhibit,
      { matterId: string; workspaceId: string; exhibitId: string }
    >({
      query: ({ matterId, workspaceId, exhibitId }) =>
        `api/matters/${matterId}/demand-review/${workspaceId}/exhibits/${exhibitId}/`,
      providesTags: (result, error, { workspaceId, exhibitId }) => [
        { type: 'DemandReviewExhibits', id: `${workspaceId}:${exhibitId}` },
      ],
    }),

    createDemandReviewExhibit: builder.mutation<
      DemandReviewExhibit,
      {
        matterId: string;
        workspaceId: string;
        data: CreateDemandReviewExhibit;
      }
    >({
      query: ({ matterId, workspaceId, data }) => ({
        url: `api/matters/${matterId}/demand-review/${workspaceId}/exhibits/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'DemandReviewExhibits', id: workspaceId },
        { type: 'DemandReviewWorkspace', id: workspaceId },
      ],
    }),

    updateDemandReviewExhibit: builder.mutation<
      DemandReviewExhibit,
      {
        matterId: string;
        workspaceId: string;
        exhibitId: string;
        data: UpdateDemandReviewExhibit;
      }
    >({
      query: ({ matterId, workspaceId, exhibitId, data }) => ({
        url: `api/matters/${matterId}/demand-review/${workspaceId}/exhibits/${exhibitId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { workspaceId, exhibitId }) => [
        { type: 'DemandReviewExhibits', id: workspaceId },
        { type: 'DemandReviewExhibits', id: `${workspaceId}:${exhibitId}` },
        { type: 'DemandReviewWorkspace', id: workspaceId },
      ],
    }),

    deleteDemandReviewExhibit: builder.mutation<
      void,
      { matterId: string; workspaceId: string; exhibitId: string }
    >({
      query: ({ matterId, workspaceId, exhibitId }) => ({
        url: `api/matters/${matterId}/demand-review/${workspaceId}/exhibits/${exhibitId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'DemandReviewExhibits', id: workspaceId },
        { type: 'DemandReviewWorkspace', id: workspaceId },
      ],
    }),

    getAvailableWorkspacesForImport: builder.query<
      Array<{
        id: string;
        name: string;
        type: 'demand_workspace' | 'demand_review';
        exhibit_count: number;
        created_at: string;
      }>,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) =>
        `api/matters/${matterId}/demand-review/${workspaceId}/exhibits/available-workspaces/`,
    }),

    importExhibits: builder.mutation<
      { imported_count: number; exhibits: DemandReviewExhibit[] },
      {
        matterId: string;
        workspaceId: string;
        sourceWorkspaceId: string;
        sourceType: 'demand_workspace' | 'demand_review';
        exhibitIds?: string[];
      }
    >({
      query: ({
        matterId,
        workspaceId,
        sourceWorkspaceId,
        sourceType,
        exhibitIds,
      }) => ({
        url: `api/matters/${matterId}/demand-review/${workspaceId}/exhibits/import/`,
        method: 'POST',
        body: {
          source_workspace_id: sourceWorkspaceId,
          source_type: sourceType,
          exhibit_ids: exhibitIds,
        },
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'DemandReviewExhibits', id: workspaceId },
        { type: 'DemandReviewWorkspace', id: workspaceId },
      ],
    }),

    // Citation endpoints
    listDemandReviewCitations: builder.query<
      DemandReviewCitation[],
      {
        matterId: string;
        workspaceId: string;
        filters?: DemandReviewCitationFilters;
      }
    >({
      query: ({ matterId, workspaceId, filters }) => {
        const params = new URLSearchParams();
        if (filters?.verification_status) {
          params.set('verification_status', filters.verification_status);
        }
        const queryString = params.toString();
        return `api/matters/${matterId}/demand-review/${workspaceId}/citations/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result, error, { workspaceId }) => [
        { type: 'DemandReviewCitations', id: workspaceId },
        ...(result ?? []).map((citation) => ({
          type: 'DemandReviewCitations' as const,
          id: `${workspaceId}:${citation.id}`,
        })),
      ],
    }),

    getDemandReviewCitation: builder.query<
      DemandReviewCitation,
      { matterId: string; workspaceId: string; citationId: string }
    >({
      query: ({ matterId, workspaceId, citationId }) =>
        `api/matters/${matterId}/demand-review/${workspaceId}/citations/${citationId}/`,
      providesTags: (result, error, { workspaceId, citationId }) => [
        { type: 'DemandReviewCitations', id: `${workspaceId}:${citationId}` },
      ],
    }),

    verifyCitation: builder.mutation<
      DemandReviewCitation,
      { matterId: string; workspaceId: string; citationId: string }
    >({
      query: ({ matterId, workspaceId, citationId }) => ({
        url: `api/matters/${matterId}/demand-review/${workspaceId}/citations/${citationId}/verify/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { workspaceId, citationId }) => [
        { type: 'DemandReviewCitations', id: workspaceId },
        { type: 'DemandReviewCitations', id: `${workspaceId}:${citationId}` },
        { type: 'DemandReviewWorkspace', id: workspaceId },
      ],
    }),
  }),
});

export const {
  // Workspace hooks
  useListDemandReviewWorkspacesQuery,
  useGetDemandReviewWorkspaceQuery,
  useCreateDemandReviewWorkspaceMutation,
  useUpdateDemandReviewWorkspaceMutation,
  useDeleteDemandReviewWorkspaceMutation,
  useUploadDemandMutation,
  useStartWorkflowMutation,
  useGetWorkflowStatusQuery,
  useMatchCitationsMutation,
  useVerifyAllCitationsMutation,
  useGetProofreadingResultsQuery,
  // Exhibit hooks
  useListDemandReviewExhibitsQuery,
  useGetDemandReviewExhibitQuery,
  useCreateDemandReviewExhibitMutation,
  useUpdateDemandReviewExhibitMutation,
  useDeleteDemandReviewExhibitMutation,
  useGetAvailableWorkspacesForImportQuery,
  useImportExhibitsMutation,
  // Citation hooks
  useListDemandReviewCitationsQuery,
  useGetDemandReviewCitationQuery,
  useVerifyCitationMutation,
} = demandReviewApiClient;
